import React, { useState } from 'react';
import {
  BuildingOfficeIcon,
  EnvelopeIcon,
  MapPinIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import ChatAvatar from './ChatAvatar';
import RoomSectionHeader from './RoomSectionHeader';
import AttachmentsAccordion from './AttachmentsAccordion';
import type { ChatRoom } from '../../types/chat';
import { useCurrentChatIdentity } from '../../hooks/chat/useCurrentChatIdentity';

interface PeerProfilePanelProps {
  room: ChatRoom;
  onClose?: () => void;
}

/**
 * Right-rail panel for direct rooms. Matches Figma `4029:196935`:
 * - Width 240 px, white background, left border
 * - Centered avatar + name + role at the top
 * - Two collapsible sections: "INFORMATION" and "ATTACHMENTS"
 */
const PeerProfilePanel: React.FC<PeerProfilePanelProps> = ({ room, onClose }) => {
  const { userId } = useCurrentChatIdentity();
  const selfUpper = (userId ?? '').toUpperCase();
  const peer = room.members.find(
    (m) => (m.user_id ?? '').toUpperCase() !== selfUpper
  );
  const [infoOpen, setInfoOpen] = useState(true);

  if (!peer) return null;

  return (
    <aside className="w-60 shrink-0 flex flex-col border-l border-border-secondary bg-surface-primary overflow-y-auto scrollbar-thin">
      {onClose && (
        <div className="flex justify-end px-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            aria-label="Close details"
            className="p-1.5 rounded-full text-text-tertiary hover:text-text-primary hover:bg-neutral-100 dark:hover:bg-neutral-800"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
      )}
      <div className="px-6 pt-2 pb-4 flex flex-col items-center text-center">
        <ChatAvatar
          src={peer.avatar_url}
          name={peer.full_name ?? peer.email}
          size={80}
          online={peer.is_online}
        />
        <p className="mt-3 text-sm font-semibold text-text-primary truncate max-w-full">
          {peer.full_name ?? peer.email}
        </p>
        {peer.role_name && (
          <p className="text-xs text-text-secondary truncate max-w-full">{peer.role_name}</p>
        )}
      </div>

      <RoomSectionHeader
        label="Information"
        open={infoOpen}
        onToggle={() => setInfoOpen((p) => !p)}
      />
      {infoOpen && (
        <ul className="px-4 py-4 space-y-3 text-sm">
          {peer.department_name && (
            <li className="flex items-start gap-2">
              <BuildingOfficeIcon className="w-4 h-4 mt-0.5 text-text-tertiary shrink-0" />
              <span className="text-text-primary truncate">{peer.department_name}</span>
            </li>
          )}
          {peer.location_name && (
            <li className="flex items-start gap-2">
              <MapPinIcon className="w-4 h-4 mt-0.5 text-text-tertiary shrink-0" />
              <span className="text-text-primary truncate">{peer.location_name}</span>
            </li>
          )}
          {peer.email && (
            <li className="flex items-start gap-2">
              <EnvelopeIcon className="w-4 h-4 mt-0.5 text-text-tertiary shrink-0" />
              <a
                href={`mailto:${peer.email}`}
                className="text-text-primary truncate hover:text-primary-500 break-all"
              >
                {peer.email}
              </a>
            </li>
          )}
        </ul>
      )}

      <AttachmentsAccordion roomId={room.id} />
    </aside>
  );
};

export default PeerProfilePanel;
